/**
 * Wer HausMeter anbietet und wie man ihn erreicht — die Angaben, auf denen jeder
 * Rechtstext steht, an **einer** Stelle für App und Webseite.
 *
 * Bis HM-024 lagen sie in `apps/web/src/lib/legal.ts`. Mit der Webseite unter
 * hausmeter.de brauchte eine zweite App dieselben Angaben — Impressum,
 * Verantwortlicher, Datenschutzbeauftragter, Aufsichtsbehörde —, und Apps
 * importieren sich nicht gegenseitig. Abgeschrieben wären es zwei Fassungen einer
 * Rechtsangabe, und die veraltete fiele erst auf, wenn sich jemand auf sie beruft.
 *
 * **Hier steht, was für beide gilt.** Was nur eine Erklärung betrifft, bleibt bei
 * ihr: die Fristen der App in `apps/web/src/lib/legal.ts`, der Webspace in
 * `apps/site/src/lib/website-legal.ts`.
 *
 * **Was wir nicht wissen, steht als `null` und wird nicht erfunden** (Regel 7,
 * Regel 9). Ein `[Telefonnummer]` auf einer veröffentlichten Seite sieht aus wie
 * ein Fehler und ist einer; eine ausgedachte Adresse wäre schlimmer.
 *
 * Das Paket importiert nichts: Es sind Angaben, keine Logik, und es wird in das
 * Browser-Bündel der App und in den Build der Webseite gezogen.
 */
/**
 * Was dieses Angebot ist — und was es **nicht** ist.
 *
 * **HausMeter ist die Software.** Geräte werden nicht hier verkauft, sondern
 * unter der Marke `shopBrand` desselben Unternehmens. Das ist keine
 * Feinheit für den Firmenbucheintrag, sondern die erste Frage, die jemand auf
 * einer Rechtsseite hat: Deckt dieser Text ab, was ich gerade getan habe?
 *
 * Ohne die Abgrenzung liest sich die Datenschutzerklärung, als beschriebe sie
 * auch die Bestellung eines Lesekopfs — Zahlungsdaten, Lieferadresse,
 * Gewährleistung. Nichts davon verarbeitet diese Anwendung, und ein Text, der
 * mehr abzudecken scheint als er abdeckt, ist an der einen Stelle zu weit und an
 * der anderen unvollständig.
 *
 * Und sie beantwortet die Frage, warum im Impressum ein Firmenname steht, der
 * nicht „HausMeter" lautet.
 */
export const PRODUCT = {
  name: "HausMeter",
  /** Die Marke, unter der Geräte verkauft werden — ein anderer Vorgang. */
  shopBrand: "Christians Technikshop",
} as const;

/** Wer den Dienst anbietet — die Angaben nach § 5 DDG. */
export const PROVIDER = {
  name: "Christians Technikshop GmbH",
  street: "Jöllenbecker Str. 180",
  city: "32130 Enger",
  country: "Deutschland",
  /** Die vertretungsberechtigte Person, mit ihrer Amtsbezeichnung. */
  representedBy: { role: "Die Geschäftsführerin", person: "Vanessa Schepelmann" },
  court: "Amtsgericht Bad Oeynhausen",
  registerNumber: "HRB 18027",
  vatId: "DE318177365",
  weeeId: "DE 68540406",
} as const;

/**
 * Die Wirtschafts-Identifikationsnummer nach § 139c AO.
 *
 * § 5 Abs. 1 Nr. 6 DDG verlangt sie im Impressum, **soweit vorhanden** — das Bundeszentralamt
 * für Steuern teilt sie seit November 2024 schrittweise zu. `null` heißt hier „nicht
 * eingetragen", nicht „nicht zugeteilt": Ob eine vorliegt, sagt der Bescheid des BZSt, nicht
 * dieses Repository. Am 17.09.2026 geprüft: **keine zugeteilt**
 * (`ECONOMIC_ID_CHECKED` in `apps/site/src/lib/website-legal.ts`). Kommt der Bescheid, gehört
 * die Nummer hierher — und in das Impressum der App, das sie bisher nicht anzeigt.
 */
export const ECONOMIC_ID: string | null = null;

/**
 * Die Kontaktwege.
 *
 * § 5 Abs. 1 Nr. 2 DDG verlangt „Angaben, die eine schnelle elektronische
 * Kontaktaufnahme und unmittelbare Kommunikation ermöglichen, einschließlich
 * der Adresse der elektronischen Post"; Art. 13 Abs. 1 lit. a DSGVO verlangt die
 * Kontaktdaten des Verantwortlichen. Beides ist mit E-Mail und Telefon erfüllt.
 *
 * **`phoneNote` sagt, wofür die Nummer nicht da ist — nicht, dass sie nicht
 * beantwortet wird.** Der Unterschied ist rechtlich erheblich: Eine im Impressum
 * genannte Nummer muss der Kommunikation mit dem Anbieter dienen. „Keine
 * Support-Hotline" grenzt den Produktsupport ab und lässt den Weg für rechtliche
 * Anliegen offen; „hier wird nicht geantwortet" machte die Angabe wertlos und
 * die Pflicht unerfüllt.
 *
 * **`phoneHref` ist die Nummer ohne Zierzeichen.** Ein `tel:`-Verweis mit
 * Leerzeichen und geklammerter Null wird von manchen Geräten falsch gewählt —
 * die `(0)` ist im internationalen Format keine zu wählende Ziffer. Angezeigt
 * wird die lesbare Form, gewählt die technische.
 */
export const CONTACT: {
  readonly email: string | null;
  readonly phone: string | null;
  readonly phoneHref: string | null;
  readonly phoneNote: string | null;
  /** Eine eigene Adresse für Datenschutzanliegen, falls sie abweicht. */
  readonly privacyEmail: string | null;
} = {
  email: "info@hausmeter.de",
  phone: "+49 (0) 5224 686 909 9",
  phoneHref: "+4952246869099",
  phoneNote: "keine Support-Hotline",
  privacyEmail: "datenschutz@hausmeter.de",
};

/**
 * Die Auftragsverarbeiter nach Art. 28 DSGVO.
 *
 * Art. 13 Abs. 1 lit. e verlangt die Empfänger oder Kategorien von Empfängern.
 * Genannt wird, **wer tatsächlich Zugriff haben könnte** — und das ist bei einem
 * selbst betriebenen Dienst nicht dessen Software, sondern der Betreiber der
 * Maschine, auf der er läuft.
 *
 * **Der Mailserver ist deshalb Hetzner und nicht „mailcow".** mailcow ist
 * Software, die wir selbst betreiben; ein Empfänger im Sinne der Vorschrift ist
 * sie nicht. Wer hier den Produktnamen einträgt, benennt einen Auftragsverarbeiter,
 * den es nicht gibt, und verschweigt den, den es gibt — der Betreiber des
 * virtuellen Servers hat den Zugriff, um den es geht.
 *
 * Beide Einträge nennen **Zweck und Sitz**: „Netcup" allein sagt nicht, wofür,
 * und zwei Einträge desselben Anbieters für verschiedene Zwecke wären sonst
 * nicht auseinanderzuhalten.
 */
export const PROCESSORS: readonly { readonly purpose: string; readonly name: string }[] = [
  {
    purpose: "Betrieb der Anwendung und Speicherung",
    name: "netcup GmbH, Karlsruhe, Deutschland",
  },
  {
    purpose: "Betrieb des E-Mail-Servers",
    name: "Hetzner Online GmbH, Gunzenhausen, Deutschland",
  },
];

/**
 * Ob die Liste der Auftragsverarbeiter vollständig ist.
 *
 * Seit dem Eintrag des Mailservers `true`. **Was damit nicht beantwortet ist:
 * der Standort des virtuellen Servers** (OQ-039) — die Erklärung sagt weiter
 * unten zu, dass keine Übermittlung in ein Drittland stattfindet, und Hetzner
 * betreibt Rechenzentren auch außerhalb der EU. Der Firmensitz beantwortet das
 * nicht; maßgeblich ist, wo die Maschine steht.
 */
export const PROCESSORS_COMPLETE = true;

/**
 * Ob bestätigt ist, dass **alle** Server in der EU stehen — bestätigt (OQ-039).
 *
 * **Der Schalter bleibt trotzdem stehen, und das ist der Punkt.** Der Firmensitz
 * eines Anbieters beantwortet die Frage nicht: netcup und Hetzner sitzen beide in
 * Deutschland, Hetzner betreibt Rechenzentren aber auch außerhalb der EU, und
 * welchen Standort ein virtueller Server bekommt, wählt der Besteller. Wer
 * künftig eine Maschine dazunimmt, prüft das erneut und setzt hier `false` —
 * eher als den Satz in der Erklärung zu suchen und umzuschreiben.
 *
 * Denn der Satz „eine Übermittlung in Drittländer findet nicht statt" ist eine
 * **Zusage**, und eine falsche wäre eine unrichtige Angabe nach Art. 13 Abs. 1
 * lit. f DSGVO — genau die Sorte Aussage, die sich plausibel liest und niemandem
 * auffällt.
 */
export const EU_ONLY_CONFIRMED = true;

/**
 * Der Datenschutzbeauftragte.
 *
 * Art. 37 Abs. 7 DSGVO verlangt, seine **Kontaktdaten zu veröffentlichen** — der
 * Name allein genügt nicht, und dieselben Kontaktdaten sind der Aufsichtsbehörde
 * mitzuteilen.
 *
 * Seine Adresse steht in `CONTACT.privacyEmail`. **Der Rückfall auf die
 * allgemeine Adresse bleibt trotzdem im Code:** Er greift, wenn jemand die
 * eigene entfernt — und eine veröffentlichte Adresse, die ins Leere läuft, ist
 * schlechter als keine, weil sie eine Erreichbarkeit behauptet. Erfunden wird
 * hier nichts (Regel 7).
 *
 * **Offen bleibt der Interessenkonflikt (OQ-038):** Nach Art. 38 Abs. 6 DSGVO
 * darf ein DSB keine Aufgabe wahrnehmen, in der er über Zwecke und Mittel der
 * Verarbeitung mitentscheidet (EuGH C-453/21). Das ist keine Formalie an einem
 * Produkt, dessen Datenmodell, Aufbewahrungsfristen und Cloud-Übertragung von
 * derselben Person entworfen werden.
 */
export const DATA_PROTECTION_OFFICER: { readonly name: string } | null = {
  name: "Christian Schepelmann",
};

/**
 * Die Aufsichtsbehörde. Sie folgt aus dem Sitz des Anbieters und ist deshalb
 * keine offene Angabe.
 */
export const SUPERVISORY_AUTHORITY = {
  name: "Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen",
  address: "Kavalleriestr. 2–4, 40213 Düsseldorf",
} as const;
